import apiClient from '@/apiClient';
import { getCookie } from '@/cookiesControls';
import {
  clearTokens, getTokens, KEY, setTokens,
} from '@/tokenManager';

export type LoginRequest = { email: string; password: string };
export type LoginTokens = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string; // ISO
  refreshTokenExpiresAt: string; // ISO
  token_type: 'Bearer' | string;
  role: string; // 'admin' | 'user'
  has_active_subscription: boolean;
  typeform_survey_session: string;
};
export type LoginResponse = {
  data: LoginTokens
};

export type ErrorResponse = { message: string };
export type ForgotPasswordResponse = ErrorResponse;
export type RefreshResponse = LoginResponse;

// const parseJsonSafe = async (res: Response) => {
//   const text = await res.text();
//   return text ? JSON.parse(text) : null;
// };

export const isExpired = (iso: string) => Date.now() >= new Date(iso).getTime();

export const isExpiredOrSoon = (iso: string, skewMs = 30_000) => Date.now() + skewMs >= new Date(iso).getTime();

export async function loginApi({
  email,
  password,
}: LoginRequest): Promise<LoginResponse> {
  const res = await apiClient.postResponse<LoginResponse | ErrorResponse>(
    'auth/login',
    {
      body: JSON.stringify({ email, password }),
    },
  );

  if (res.status !== 200) {
    throw new Error(
      (res?.response as ErrorResponse).message || 'Login failed',
    );
  }
  return res.response as LoginResponse;
}

export async function forgotPasswordApi(email: string) {
  const res = await apiClient.postResponse<ForgotPasswordResponse>(
    'auth/send-change-password-link',
    {
      body: JSON.stringify({ email }),
    },
  );
  if (res.status !== 200) throw new Error(res?.response.message || 'Failed to send reset link');
  return res.response;
}

export async function refreshTokens(
  refreshToken: string,
): Promise<RefreshResponse> {
  const res = await apiClient.postResponse<
  RefreshResponse | ErrorResponse
  >('auth/refresh', {
    body: JSON.stringify({ refreshToken }),
  });

  if (res.status !== 200) {
    throw new Error(
      (res?.response as ErrorResponse).message
        || 'Failed to refresh session',
    );
  }
  return res.response as RefreshResponse;
}

let refreshingPromise: Promise<RefreshResponse> | null = null;

async function refreshAndSetTokens(refreshToken: string) {
  refreshingPromise ||= refreshTokens(refreshToken);
  try {
    const fresh = await refreshingPromise;
    refreshingPromise = null;

    const remember = !!getCookie(KEY.accessTokenExpiresAt);
    setTokens(fresh.data, remember);
    return getTokens()!;
  } catch (e) {
    refreshingPromise = null;
    clearTokens();
    throw e;
  }
}

export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  retries = 2,
): Promise<Response> {
  let tokens = getTokens();
  if (!tokens) {
    window.location.href = '/login';
    throw new Error('Not authenticated');
  }
  const tokenType = tokens.tokenType || 'Bearer';

  if (isExpiredOrSoon(tokens.accessTokenExpiresAt)) {
    tokens = await refreshAndSetTokens(tokens.refreshToken);
  }

  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `${tokenType} ${tokens.accessToken}`);
  headers.set('Content-Type', headers.get('Content-Type') || 'application/json');

  const res = await fetch(input, { ...init, headers });

  if (res.status === 401 && retries > 0) {
    try {
      tokens = await refreshAndSetTokens(tokens.refreshToken);
      const retryHeaders = new Headers(init.headers || {});
      retryHeaders.set('Authorization', `${tokens.tokenType || 'Bearer'} ${tokens.accessToken}`);
      retryHeaders.set('Content-Type', retryHeaders.get('Content-Type') || 'application/json');

      return await fetchWithAuth(input, { ...init, headers: retryHeaders }, retries - 1);
    } catch {
      clearTokens();
      window.location.href = '/login';
      throw new Error('Unauthorized after refresh attempt');
    }
  }

  if (res.status === 401) {
    clearTokens();
    window.location.href = '/login';
    throw new Error('Unauthorized after refresh attempt');
  }

  return res;
}

export async function resetPasswordApiAuthed(params: {
  password: string;
  password_confirmation: string;
}) {
  const res = await apiClient.postResponseWithAuth('user/change-password', {
    body: JSON.stringify({
      password: params.password,
      password_confirmation: params.password_confirmation,
    }),
  });

  if (res.status !== 200) {
    throw new Error(
      (res?.response as ErrorResponse).message
        || 'Failed to reset password',
    );
  }
  return res.response;
}

export async function resetPasswordApi(params: {
  email: string;
  password: string;
  password_confirmation: string;
  token?: string;
}) {
  const storedTokens = getTokens();
  const token = params.token || (storedTokens && storedTokens.accessToken);

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const res = await apiClient.postResponse<
  RefreshResponse | ErrorResponse
  >('auth/change-password', {
    body: JSON.stringify({
      email: params.email,
      token,
      password: params.password,
      password_confirmation: params.password_confirmation,
    }),
  });

  if (res.status !== 200) {
    throw new Error(
      (res?.response as ErrorResponse).message
        || 'Failed to reset password',
    );
  }
  return res.response;
}
